import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.jobsaigent.com',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL || 'https://api.jobsaigent.com'}/api/auth/refresh/`,
            { refresh: refreshToken }
          );

          const newAccessToken = response.data.access;
          localStorage.setItem('access_token', newAccessToken);
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// ==================== AI Agent API Endpoints ====================

/**
 * Start a new AI conversation
 */
export const startAIConversation = async () => {
  try {
    const response = await api.get('/api/ai/start/');
    return response.data;
  } catch (error) {
    console.error('Error starting AI conversation:', error);
    throw error;
  }
};

/**
 * Send a message to the AI agent
 * @param {Object} data - { user_message, conversation_history, question_number }
 */
export const sendAIMessage = async (data) => {
  try {
    const response = await api.post('/api/ai/chat/', data);
    return response.data;
  } catch (error) {
    console.error('Error sending AI message:', error);
    throw error;
  }
};

/**
 * Get detailed template information from AI
 * @param {Array<string>} templateIds - Array of template IDs
 */
export const getAITemplateDetails = async (templateIds) => {
  try {
    const response = await api.post('/api/ai/template-details/', {
      template_ids: templateIds
    });
    return response.data;
  } catch (error) {
    console.error('Error getting AI template details:', error);
    throw error;
  }
};

/**
 * Generate explanation for template recommendation
 * @param {string} templateId - Template ID
 * @param {string} userContext - User context string
 */
export const generateAIExplanation = async (templateId, userContext) => {
  try {
    const response = await api.post('/api/ai/explanation/', {
      template_id: templateId,
      user_context: userContext
    });
    return response.data;
  } catch (error) {
    console.error('Error generating AI explanation:', error);
    throw error;
  }
};

/**
 * Generate resume content from AI conversation
 * @param {Array} conversationHistory - Array of conversation messages
 */
export const generateAIResumeContent = async (conversationHistory) => {
  try {
    const response = await api.post('/api/ai/generate-resume/', {
      conversation_history: conversationHistory
    });
    return response.data;
  } catch (error) {
    console.error('Error generating AI resume content:', error);
    throw error;
  }
};

/**
 * Analyze a resume and get rating, summary, and suggestions
 * @param {Object} resumeData - Resume data object from builder
 * @param {string} jobTitle - Optional target job title
 * @param {string} jobDescription - Optional job description
 */
export const analyzeResume = async (resumeData, jobTitle = null, jobDescription = null) => {
  try {
    const response = await api.post('/api/ai/analyze-resume/', {
      resume_data: resumeData,
      job_title: jobTitle,
      job_description: jobDescription
    }, { timeout: 60000 });
    return response.data;
  } catch (error) {
    console.error('Error analyzing resume:', error);
    throw error;
  }
};

/**
 * Analyze an uploaded resume file and get rating, summary, and suggestions
 * @param {File} file - Uploaded resume file (PDF, DOCX, TXT)
 * @param {string} jobTitle - Optional target job title
 * @param {string} jobDescription - Optional job description
 */
export const analyzeResumeFile = async (file, jobTitle = null, jobDescription = null) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (jobTitle) formData.append('job_title', jobTitle);
    if (jobDescription) formData.append('job_description', jobDescription);

    const response = await api.post('/api/ai/analyze-resume/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });

    return response.data;
  } catch (error) {
    console.error('Error analyzing uploaded resume file:', error);
    throw error;
  }
};

/**
 * Create a Stripe checkout session for a plan
 * @param {string} plan - 'enterprise', 'pack5', or 'pack10'
 * @param {string} successUrl
 * @param {string} cancelUrl
 */
export const createCheckoutSession = async (plan, successUrl, cancelUrl) => {
  try {
    const response = await api.post('/api/billing/create-checkout-session/', {
      plan,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
    return response.data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

export default api;
